import './EditIncompleteDialog.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useContext, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ToastContext from '../../../../../../context/ToastContext';
import useAxios from '../../../../../../hooks/useAxios';
import useMediaQuery from '../../../../../../hooks/useMediaQuery';
import {
  errorToast,
  successToast,
} from '../../../../../../utils/helpers/primereact';
import DialogSpinner from '../../../../../Dialogs/DialogSpinner/DialogSpinner';
import { IncompleteOrders } from '../../Incomplete.functions';
import {
  FormValues,
  getDefaultValues,
  getValidationSchema,
} from './EditIncompleteDialog.functions';
import FormContainer from './FormContainer';

type Props = {
  visible: boolean;
  data?: IncompleteOrders;
  onHide: () => void;
  reload: () => void;
  onEdit?: (id: number) => void;
};

function EditIncompleteDialog({
  visible,
  data,
  onHide,
  reload,
  onEdit,
}: Props) {
  const { t } = useTranslation();
  const toastRef = useContext(ToastContext)?.toastRef!;
  const validationSchema = useMemo(() => getValidationSchema(t), [t]);
  const defaultValues = useMemo(() => getDefaultValues(data), [data]);

  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const { handleSubmit, reset } = methods;

  const { reload: formSubmitRequest, isLoading: isFormSubmitRequestLoading } =
    useAxios();

  function handleFormSubmission(values: FormValues) {
    formSubmitRequest({
      url: `/v2/address/incomplete/${data?.id}`,
      payload: values,
      method: 'PUT',
      successCallback: (data: any) => {
        successToast(
          toastRef,
          t('Address Updated'),
          t('Address updated successfully.')
        );

        reset();
        onEdit?.(data?.id);
        reload();
        onHide();
      },
      errorCallback: (err: any) => {
        console.log('err ', err);
        errorToast(
          toastRef,
          t('Address Update Failed'),
          t(
            'An error occurred while trying to edit Address. Please check your inputs.'
          )
        );
      },
    });
  }

  // useEffect(() => {
  //   reset();
  // }, [visible, reset]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const isOnMobile = useMediaQuery('(max-width: 768px)');

  const dialogFooter = (
    <>
      <Button
        type="button"
        label={t('Cancel')}
        className="p-button-text"
        onClick={onHide}
      />

      <Button
        type="submit"
        form="order-incomplete-edit-form"
        label={t('Save changes')}
        disabled={isFormSubmitRequestLoading}
        className="p-button-primary"
        data-cy="submit"
      />
    </>
  );

  const dialogHeader = <h3>{data?.orderSerialNumber}</h3>;

  return (
    <FormProvider {...methods}>
      <Dialog
        visible={visible}
        className="create-edit-incomplete-dialog"
        header={dialogHeader}
        footer={dialogFooter}
        onHide={onHide}
        blockScroll
        maximizable
        maximized={isOnMobile}
      >
        {false ? (
          <DialogSpinner />
        ) : (
          <form
            id="order-incomplete-edit-form"
            onSubmit={handleSubmit(handleFormSubmission)}
          >
            <h3>{t('Incomplete Address')}</h3>
            <table cellPadding="0" cellSpacing="0">
              <tbody>
                <tr>
                  <td>
                    <b>{t('City')}</b>
                  </td>
                  <td>{data?.address?.cityName ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Municipality')}</b>
                  </td>
                  <td>{data?.address?.municipalityName ?? '-'}</td>
                </tr>

                <tr>
                  <td>
                    <b>{t('Place')}</b>
                  </td>
                  <td>{data?.address?.placeName ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Street')}</b>
                  </td>
                  <td>{data?.address?.rawName ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t('House number')}</b>
                  </td>
                  <td>{data?.address?.housenumber ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Entrance')}</b>
                  </td>
                  <td>{data?.address?.entrance ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Apartment')}</b>
                  </td>
                  <td>{data?.address?.apartment ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Post Code')}</b>
                  </td>
                  <td>{data?.address?.postcode ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Status')}</b>
                  </td>
                  <td>{data?.status ?? '-'}</td>
                </tr>
                <tr>
                  <td>
                    <b>{t('Last modified')}</b>
                  </td>
                  <td>
                    {data?.modifiedAt
                      ? dayjs(data.modifiedAt).format('DD MMM YYYY, HH:mm:ss')
                      : '-'}
                  </td>
                </tr>
              </tbody>
            </table>
            <FormContainer />
          </form>
        )}
      </Dialog>
    </FormProvider>
  );
}

export default EditIncompleteDialog;
